
import { defineComponent, ref, onMounted, computed } from "vue";
import { usePlugin } from "@/plugins";
import {
  CourseRelation,
  Course,
  DropdownDto,
  GetCourseRelationResponse,
  UpsertCourseRelationRequest,
} from "@/types";
import InputCourse from "@/components/InputCourse.vue";
import { useVuelidate } from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import { clone } from "ramda";
import { FilterMatchMode } from "primevue/api";

export default defineComponent({
  name: "CourseRelationList",
  components: { InputCourse },
  setup() {
    const initial: CourseRelation = {
      id: 0,
      divisionCode: null,
      departmentCode: null,
      position: null,
      level: null,
      courseId: null,
      course: null,
      divisionDescription: null,
      departmentDescription: null,
    };
    const { axios, error, success, setLoading, formatAppDate } = usePlugin();
    const relations = ref<CourseRelation[]>([]);
    const departments = ref<DropdownDto[]>([]);
    const divisions = ref<DropdownDto[]>([]);
    const positions = ref<DropdownDto[]>([]);
    const relation = ref<CourseRelation>(initial);
    const relationFilter = ref({
      global: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
    });
    const showAddDialog = ref(false);
    const showDivision = (divisionCode: string) => {
      return !divisionCode
        ? "-"
        : `${divisionCode} : ${divisions.value.find(
            (x) => x.code === divisionCode
          )?.description ?? "N/A"}`;
    };

    const showDepartment = (departmentCode: string) => {
      return !departmentCode
        ? "-"
        : `${departmentCode} : ${departments.value.find(
            (x) => x.code === departmentCode
          )?.description ?? "N/A"}`;
    };

    const checkDuplicate = () => {
      return (
        relations.value.findIndex(
          (e) =>
            e.divisionCode === relation.value.divisionCode &&
            e.departmentCode === relation.value.departmentCode &&
            e.level === relation.value.level &&
            e.courseId === relation.value.courseId
        ) < 0
      );
    };
    const rules = computed(() => ({
      relation: {
        duplicate: {
          duplicate: helpers.withMessage(
            "พบข้อมูลแล้ว กรุณาระบุข้อมูลชุดอื่น ๆ",
            checkDuplicate
          ),
        },
        courseId: {
          required: helpers.withMessage("กรุณาระบุ Course", required),
        },
      },
    }));
    const v$ = useVuelidate(rules, { relation }, { $autoDirty: true });

    const loadDocument = async () => {
      setLoading(true);
      try {
        const result = await axios.post<GetCourseRelationResponse>(
          "api/v1/services/courseRelation",
          {}
        );
        relations.value = result.data.relations ?? [];
        divisions.value = result.data.divisions ?? [];
        positions.value = result.data.positions ?? [];
        departments.value = result.data.departments ?? [];
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onNew = () => {
      relation.value = clone(initial);
      v$.value.$reset();
      showAddDialog.value = true;
      
    };

    const onSubmitNew = async () => {
      v$.value.$touch();
      if (v$.value.$invalid) return;

      try {
        await axios.post("api/v1/services/courseRelation/upsert", {
          relation: [relation.value],
        } as UpsertCourseRelationRequest);
        success("ลบข้อมูลแล้ว");
        loadDocument();
        showAddDialog.value = false;
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onDelete = async (index: number) => {
      setLoading(true);
      try {
        await axios.post("api/v1/services/courseRelation/upsert", {
          relation: [relations.value[index]],
          delete: true,
        } as UpsertCourseRelationRequest);
        success("ลบข้อมูลแล้ว");
        relations.value.splice(index, 1);
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };
    const onCopy = (index: number) => {
      relation.value = clone(relations.value[index]);
      relation.value.id = 0;
      v$.value.$reset();
      showAddDialog.value = true;
    };

    onMounted(() => loadDocument());

    return {
      relations,
      relationFilter,
      showAddDialog,
      relation,
      departments,
      divisions,
      positions,
      onNew,
      v$,
      onSubmitNew,
      onDelete,
      onCopy,
      showDivision,
      showDepartment,
    };
  },
});
